import React, { useEffect, useState } from "react";
import { fetchCompanySettings, saveCompanySettings } from "../../shared/companySettingsApi";
import { ImageUploadField } from "../../shared/ImageUploadField";
import { IconRefresh, IconSave } from "../../shared/toolbarIcons";

const SAVED_PASSWORD_MASK = "••••••••";

const emptyForm = () => ({
  companyName: "",
  companyEmail: "",
  phoneNumber: "",
  whatsAppNumber: "",
  address: "",
  websiteUrl: "",
  companyLogo: "",
  facebookUrl: "",
  instagramUrl: "",
  twitterUrl: "",
  linkedInUrl: "",
  emailAddress: "",
  emailPassword: ""
});

function PasswordInput({ value, savedPassword, isSavedMask, onChange, onFocus, required }) {
  const [visible, setVisible] = useState(false);

  const displayValue = isSavedMask
    ? visible
      ? savedPassword || SAVED_PASSWORD_MASK
      : SAVED_PASSWORD_MASK
    : value;

  return (
    <div className="password-input-wrap">
      <input
        type={visible ? "text" : "password"}
        autoComplete="new-password"
        required={required}
        value={displayValue}
        onChange={onChange}
        onFocus={onFocus}
        readOnly={isSavedMask}
      />
      <button
        type="button"
        className="password-input-toggle"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        title={visible ? "Hide password" : "Show password"}
      >
        {visible ? (
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}

export function CompanySettingsPage() {
  const [form, setForm] = useState(emptyForm);
  const [hasEmailPassword, setHasEmailPassword] = useState(false);
  const [savedEmailPassword, setSavedEmailPassword] = useState("");
  const [passwordUnchanged, setPasswordUnchanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    const res = await fetchCompanySettings();
    if (!res.ok) {
      setError(res.error ?? "Failed to load company settings.");
      setLoading(false);
      return;
    }

    const data = res.data;
    const hasPwd = Boolean(data.hasEmailPassword);
    setForm({
      companyName: data.companyName ?? "",
      companyEmail: data.companyEmail ?? "",
      phoneNumber: data.phoneNumber ?? "",
      whatsAppNumber: data.whatsAppNumber ?? "",
      address: data.address ?? "",
      websiteUrl: data.websiteUrl ?? "",
      companyLogo: data.companyLogo ?? "",
      facebookUrl: data.facebookUrl ?? "",
      instagramUrl: data.instagramUrl ?? "",
      twitterUrl: data.twitterUrl ?? "",
      linkedInUrl: data.linkedInUrl ?? "",
      emailAddress: data.emailAddress ?? "",
      emailPassword: hasPwd ? SAVED_PASSWORD_MASK : ""
    });
    setHasEmailPassword(hasPwd);
    setSavedEmailPassword(hasPwd ? (data.emailPassword ?? "") : "");
    setPasswordUnchanged(hasPwd);
    setLoading(false);
  };

  useEffect(() => {
    void loadSettings();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const payload = {
      companyName: form.companyName.trim(),
      companyEmail: form.companyEmail.trim(),
      phoneNumber: form.phoneNumber.trim(),
      whatsAppNumber: form.whatsAppNumber.trim() || null,
      address: form.address.trim() || null,
      websiteUrl: form.websiteUrl.trim() || null,
      companyLogo: form.companyLogo.trim() || null,
      facebookUrl: form.facebookUrl.trim() || null,
      instagramUrl: form.instagramUrl.trim() || null,
      twitterUrl: form.twitterUrl.trim() || null,
      linkedInUrl: form.linkedInUrl.trim() || null,
      emailAddress: form.emailAddress.trim()
    };

    const passwordChanged =
      !passwordUnchanged &&
      form.emailPassword.trim() &&
      form.emailPassword !== SAVED_PASSWORD_MASK;

    if (passwordChanged) {
      payload.emailPassword = form.emailPassword;
    }

    const res = await saveCompanySettings(payload);
    if (!res.ok) {
      let message = res.error ?? "Failed to save company settings.";
      try {
        const parsed = JSON.parse(message);
        message = parsed.message ?? parsed.Message ?? message;
      } catch {
        // keep raw message
      }
      setError(message);
      setSaving(false);
      return;
    }

    await loadSettings();
    setSaving(false);
  };

  const handlePasswordFocus = () => {
    if (!passwordUnchanged) return;
    setPasswordUnchanged(false);
    setForm((prev) => ({ ...prev, emailPassword: "" }));
  };

  const handlePasswordChange = (e) => {
    const next = e.target.value;
    if (passwordUnchanged) {
      setPasswordUnchanged(false);
      setForm((prev) => ({ ...prev, emailPassword: next === SAVED_PASSWORD_MASK ? "" : next }));
      return;
    }
    setForm((prev) => ({ ...prev, emailPassword: next }));
  };

  if (loading) {
    return (
      <div className="company-settings-page">
        <p className="company-settings-loading">Loading company settings...</p>
      </div>
    );
  }

  return (
    <div className="company-settings-page">
      <div className="company-settings-toolbar">
        <h2>Company Settings</h2>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="submit"
            form="company-settings-form"
            disabled={saving}
            title="Save"
            aria-label="Save company settings"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void loadSettings()}
            disabled={saving}
            title="Refresh"
            aria-label="Refresh company settings"
          >
            <IconRefresh />
          </button>
        </div>
      </div>

      <form id="company-settings-form" onSubmit={onSubmit} className="company-settings-stack">
        <div className="card">
          <div className="card-header">
            <h2>Company Information</h2>
          </div>
          <div className="card-body">
            <div className="form form-fields-bottomline">
              <div className="form-row form-row--3">
                <label className="field">
                  <span>Company Name *</span>
                  <input
                    type="text"
                    required
                    value={form.companyName}
                    onChange={(e) => setForm((p) => ({ ...p, companyName: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>Company Email *</span>
                  <input
                    type="email"
                    required
                    value={form.companyEmail}
                    onChange={(e) => setForm((p) => ({ ...p, companyEmail: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>Phone Number *</span>
                  <input
                    type="text"
                    required
                    value={form.phoneNumber}
                    onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
                  />
                </label>
              </div>
              <div className="form-row form-row--3">
                <label className="field">
                  <span>WhatsApp Number</span>
                  <input
                    type="text"
                    value={form.whatsAppNumber}
                    onChange={(e) => setForm((p) => ({ ...p, whatsAppNumber: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>Website URL</span>
                  <input
                    type="url"
                    value={form.websiteUrl}
                    onChange={(e) => setForm((p) => ({ ...p, websiteUrl: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>Address</span>
                  <textarea
                    rows={1}
                    value={form.address}
                    onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                  />
                </label>
              </div>
              <div className="form-row form-row--3">
                <ImageUploadField
                  label="Company Logo"
                  value={form.companyLogo}
                  onChange={(companyLogo) => setForm((p) => ({ ...p, companyLogo }))}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>Social Media</h2>
          </div>
          <div className="card-body">
            <div className="form form-fields-bottomline">
              <div className="form-row form-row--4">
                <label className="field">
                  <span>Facebook</span>
                  <input
                    type="url"
                    value={form.facebookUrl}
                    onChange={(e) => setForm((p) => ({ ...p, facebookUrl: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>Instagram</span>
                  <input
                    type="url"
                    value={form.instagramUrl}
                    onChange={(e) => setForm((p) => ({ ...p, instagramUrl: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>Twitter / X</span>
                  <input
                    type="url"
                    value={form.twitterUrl}
                    onChange={(e) => setForm((p) => ({ ...p, twitterUrl: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>LinkedIn</span>
                  <input
                    type="url"
                    value={form.linkedInUrl}
                    onChange={(e) => setForm((p) => ({ ...p, linkedInUrl: e.target.value }))}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>OTP Email Account</h2>
          </div>
          <div className="card-body">
            <div className="form form-fields-bottomline">
              <div className="form-row form-row--3">
                <label className="field">
                  <span>Email Address *</span>
                  <input
                    type="email"
                    required
                    value={form.emailAddress}
                    onChange={(e) => setForm((p) => ({ ...p, emailAddress: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>Password {!hasEmailPassword && "*"}</span>
                  <PasswordInput
                    value={form.emailPassword}
                    savedPassword={savedEmailPassword}
                    isSavedMask={passwordUnchanged}
                    required={!hasEmailPassword}
                    onFocus={handlePasswordFocus}
                    onChange={handlePasswordChange}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="error-banner">{error}</div>}
      </form>
    </div>
  );
}
